import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import { getUsers, getAreas, getDepartments, getTags, inviteUser, toggleUserActive, forcePasswordChange, getPendingInvitations } from '../utils/api';
import axios from 'axios';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { addToast } = useToast();
  
  // Get active tab from route
  const getTabFromPath = (path) => {
    if (path.includes('/users')) return 'users';
    if (path.includes('/departments')) return 'departments';
    if (path.includes('/areas')) return 'areas';
    if (path.includes('/tags')) return 'tags';
    return '';
  };
  
  const activeTab = getTabFromPath(location.pathname);
  const [users, setUsers] = useState([]);
  const [areas, setAreas] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'Usuario', department_name: '' });
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [pendingInvitations, setPendingInvitations] = useState([]);

  const tabs = [
    { key: 'users', label: 'Usuarios' },
    { key: 'tags', label: 'Etiquetas' }
  ];

  useEffect(() => {
    if (activeTab) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      if (activeTab === 'users') {
        const [usersRes, invRes] = await Promise.all([getUsers(), getPendingInvitations()]);
        setUsers(usersRes.data);
        setPendingInvitations(invRes.data);
      } else if (activeTab === 'tags') {
        const res = await getTags();
        setTags(res.data);
      }
    } catch (error) {

    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingItem(null);
    if (activeTab === 'users') {
      setFormData({ 
        name: '', 
        email: '', 
        password: '', 
        role: 'Usuario', 
        department_name: ''  // Departamento único
      });
    } else if (activeTab === 'tags') {
      setFormData({ name: '', color: '#E4B68A' });
    }
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData(item);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/${activeTab}/${editingItem.id}`, formData);
        addToast('✨ Actualizado correctamente', 'success');
      } else {
        await axios.post(`${API}/${activeTab}`, formData);
        addToast('✨ Creado correctamente', 'success');
      }
      setShowForm(false);
      loadData();
    } catch (error) {

      addToast(error.response?.data?.detail || 'Error al guardar', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este elemento?')) return;
    try {
      await axios.delete(`${API}/${activeTab}/${id}`);
      addToast('✨ Eliminado correctamente', 'success');
      loadData();
    } catch (error) {

      addToast('Error al eliminar', 'error');
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    setInviteResult(null);
    try {
      const res = await inviteUser(inviteForm);
      setInviteResult({ success: true, ...res.data });
      addToast('✉️ Invitación enviada', 'success');
    } catch (error) {
      setInviteResult({ success: false, error: error.response?.data?.detail || 'Error al enviar invitación' });
    }
    setInviting(false);
  };

  const handleToggleActive = async (userId, currentlyActive) => {
    const action = currentlyActive ? 'desactivar' : 'activar';
    if (!window.confirm(`¿${action.charAt(0).toUpperCase() + action.slice(1)} este usuario?`)) return;
    try {
      const res = await toggleUserActive(userId);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active: res.data.is_active } : u));
      addToast(res.data.is_active ? '✅ Usuario activado' : '🔒 Usuario desactivado', 'success');
    } catch (error) {
      addToast(error.response?.data?.detail || 'Error al cambiar estado', 'error');
    }
  };

  const handleForcePasswordChange = async (userId, userName) => {
    if (!window.confirm(`¿Forzar cambio de contraseña para ${userName}?`)) return;
    try {
      await forcePasswordChange(userId);
      addToast('🔑 Se solicitará cambio de contraseña en el próximo ingreso', 'success');
    } catch (error) {
      addToast(error.response?.data?.detail || 'Error', 'error');
    }
  };

  const currentData = { users, areas, departments, tags }[activeTab] || [];

  // Landing sin tab
  if (!activeTab) {
    return (
      <div className="min-h-screen">
        <div className="px-6 py-5" style={{
          background: 'rgba(10, 26, 20, 0.8)',
          backdropFilter: 'blur(20px)',
          borderBottom: '2px solid rgba(0, 208, 132, 0.3)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(0,208,132,0.1)'
        }}>
          <h1 className="text-2xl font-black" style={{
            background: 'linear-gradient(135deg, #00D084 0%, #00FFB3 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Administración
          </h1>
          <p className="text-sm font-semibold mt-1" style={{ color: '#A0F0D0' }}>Gestión de usuarios y catálogos</p>
        </div>
        
        <div className="p-6">
          <div className="max-w-6xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <motion.button
                  whileHover={{ y: -2 }}
                  onClick={() => navigate('/admin/users')}
                  className="decko-card decko-card-hover p-8 text-left col-span-2"
                >
                  <div className="text-5xl mb-4">👥</div>
                  <h3 className="text-xl font-bold text-foreground mb-2">Usuarios</h3>
                  <p className="text-muted-foreground text-sm">
                    Gestionar usuarios. Cada usuario = un departamento único
                  </p>
                </motion.button>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => navigate('/admin/tags')}
                  className="decko-card decko-card-hover w-full p-5 text-left"
                >
                  <div className="text-3xl mb-2">🏷️</div>
                  <div className="text-sm font-semibold text-foreground">Tags</div>
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  // Content con tab activo
  return (
    <div className="p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin')}
              className="text-muted-foreground hover:text-foreground p-2 rounded-lg hover:bg-secondary transition-colors"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div>
              <h2 className="text-xl font-bold text-foreground">
                {tabs.find(t => t.key === activeTab)?.label}
              </h2>
              <p className="text-sm text-muted-foreground">
                Gestionar {tabs.find(t => t.key === activeTab)?.label.toLowerCase()}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {activeTab === 'users' && (
              <button
                onClick={() => { setShowInviteModal(true); setInviteResult(null); setInviteForm({ email: '', role: 'Usuario', department_name: '' }); }}
                className="btn-secondary flex items-center gap-2"
              >
                ✉️ Invitar Usuario
              </button>
            )}
            {activeTab !== 'departments' && (
              <button
                onClick={handleCreate}
                className="btn-primary"
              >
                + Crear
              </button>
            )}
            {activeTab === 'departments' && (
              <div className="px-4 py-2 bg-muted rounded-xl text-muted-foreground text-sm font-semibold">
                ℹ️ Los departamentos se gestionan desde Usuarios
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="decko-card p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Cargando...</p>
          </div>
        ) : (
          <div className="decko-card overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  {activeTab === 'users' && (
                    <>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Responsable</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Rol</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Departamento</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Estado</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-foreground">Acciones</th>
                    </>
                  )}
                  {(activeTab === 'areas' || activeTab === 'tags') && (
                    <>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Nombre</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Color</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-foreground">Acciones</th>
                    </>
                  )}
                  {activeTab === 'departments' && (
                    <>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Nombre</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Área</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Encargado</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-foreground">Acciones</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {currentData.map((item, idx) => (
                  <tr key={item.id} className={idx % 2 === 0 ? 'bg-background' : 'bg-card'}>
                    {activeTab === 'users' && (
                      <>
                        <td className="px-4 py-3 text-sm text-foreground">{item.name}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">{item.email}</td>
                        <td className="px-4 py-3 text-sm text-foreground">{item.role}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {item.department_name || '-'}
                        </td>
                        <td className="px-4 py-3">
                          {item.is_active !== false ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
                              style={{ background: 'rgba(0,208,132,0.12)', color: '#00D084' }}>
                              ● Activo
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
                              style={{ background: 'rgba(239,68,68,0.12)', color: '#EF4444' }}>
                              ● Inactivo
                            </span>
                          )}
                        </td>
                      </>
                    )}
                    {(activeTab === 'areas' || activeTab === 'tags') && (
                      <>
                        <td className="px-4 py-3 text-sm text-foreground">{item.name}</td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded" style={{ backgroundColor: item.color }} />
                            <span className="text-muted-foreground">{item.color}</span>
                          </div>
                        </td>
                      </>
                    )}
                    {activeTab === 'departments' && (
                      <>
                        <td className="px-4 py-3 text-sm text-foreground">{item.name}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {areas.find(a => a.id === item.area_id)?.name || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground">
                          <div>
                            {item.manager_name}
                            {item.manager_user && (
                              <span className="ml-2 px-2 py-0.5 bg-primary/20 text-primary text-xs rounded">
                                Vinculado
                              </span>
                            )}
                          </div>
                        </td>
                      </>
                    )}
                    <td className="px-4 py-3 text-right">
                      {activeTab === 'departments' ? (
                        <span className="text-xs text-muted-foreground italic">
                          Gestionar en Usuarios
                        </span>
                      ) : activeTab === 'users' ? (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleForcePasswordChange(item.id, item.name)}
                            className="text-xs font-semibold px-2.5 py-1 rounded-lg transition-colors"
                            style={{ background: 'rgba(245,158,11,0.1)', color: '#F59E0B' }}
                            title="Forzar cambio de contraseña en próximo ingreso"
                          >
                            🔑 Cambio
                          </button>
                          <button
                            onClick={() => handleToggleActive(item.id, item.is_active !== false)}
                            className="text-xs font-semibold px-2.5 py-1 rounded-lg transition-colors"
                            style={item.is_active !== false
                              ? { background: 'rgba(239,68,68,0.1)', color: '#EF4444' }
                              : { background: 'rgba(0,208,132,0.1)', color: '#00D084' }
                            }
                          >
                            {item.is_active !== false ? 'Desactivar' : 'Activar'}
                          </button>
                          <button
                            onClick={() => handleEdit(item)}
                            className="text-primary hover:underline text-xs font-semibold"
                          >
                            Editar
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="text-destructive hover:underline text-xs font-semibold"
                          >
                            Eliminar
                          </button>
                        </div>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEdit(item)}
                            className="text-primary hover:underline text-sm mr-3 font-semibold"
                          >
                            Editar
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="text-destructive hover:underline text-sm font-semibold"
                          >
                            Eliminar
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pending Invitations */}
        {activeTab === 'users' && pendingInvitations.length > 0 && (
          <div className="mt-6 decko-card overflow-hidden">
            <div className="px-4 py-3 border-b border-border flex items-center gap-2" style={{ background: 'rgba(99,102,241,0.06)' }}>
              <span className="text-sm font-bold" style={{ color: '#6366F1' }}>✉️ Invitaciones Pendientes</span>
              <span className="px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: 'rgba(99,102,241,0.15)', color: '#6366F1' }}>
                {pendingInvitations.length}
              </span>
              <span className="text-xs text-muted-foreground ml-1">Enviadas pero no aceptadas</span>
            </div>
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-foreground">Email</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-foreground">Rol</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-foreground">Enviada</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-foreground">Expira</th>
                </tr>
              </thead>
              <tbody>
                {pendingInvitations.map((inv, idx) => {
                  const expiresAt = new Date(inv.expires_at);
                  const isExpired = expiresAt < new Date();
                  return (
                    <tr key={inv.id || idx} className={idx % 2 === 0 ? 'bg-background' : 'bg-card'}>
                      <td className="px-4 py-2.5 text-sm text-foreground">{inv.email}</td>
                      <td className="px-4 py-2.5 text-sm text-muted-foreground">{inv.role}</td>
                      <td className="px-4 py-2.5 text-xs text-muted-foreground">
                        {new Date(inv.created_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
                          style={isExpired
                            ? { background: 'rgba(239,68,68,0.12)', color: '#EF4444' }
                            : { background: 'rgba(245,158,11,0.12)', color: '#F59E0B' }
                          }>
                          {isExpired ? '⚠ Expirada' : expiresAt.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Invite Modal */}
        {showInviteModal && (
          <>
            <div className="fixed inset-0 bg-black/50 z-40" onClick={() => { setShowInviteModal(false); setInviteResult(null); }} />
            <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
              <div className="bg-card border border-border rounded-2xl p-6 max-w-sm w-full shadow-2xl">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h3 className="text-base font-bold text-foreground">Invitar Usuario</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">Recibirá un email con el link para registrarse</p>
                  </div>
                  <button onClick={() => { setShowInviteModal(false); setInviteResult(null); }}
                    className="text-muted-foreground hover:text-foreground transition-colors p-1">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>

                {!inviteResult ? (
                  <form onSubmit={handleInvite} className="space-y-4">
                    <div>
                      <label className="block text-xs font-semibold mb-1.5 text-foreground">
                        Email <span className="text-destructive">*</span>
                      </label>
                      <input
                        type="email"
                        value={inviteForm.email}
                        onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))}
                        required
                        placeholder="correo@empresa.com"
                        className="input-decko w-full text-sm"
                        autoFocus
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold mb-1.5 text-foreground">Rol</label>
                      <select
                        value={inviteForm.role}
                        onChange={e => setInviteForm(f => ({ ...f, role: e.target.value }))}
                        className="input-decko w-full text-sm"
                      >
                        <option value="Usuario">Usuario</option>
                        <option value="Gerencia">Gerencia</option>
                        <option value="Presidencia">Presidencia</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold mb-1.5 text-foreground">
                        Departamento <span className="text-destructive">*</span>
                      </label>
                      <input
                        type="text"
                        value={inviteForm.department_name}
                        onChange={e => setInviteForm(f => ({ ...f, department_name: e.target.value }))}
                        required
                        placeholder="Ej: Operaciones, Logística, Ventas"
                        className="input-decko w-full text-sm"
                      />
                    </div>
                    <div className="flex gap-3 pt-2">
                      <button type="button" onClick={() => setShowInviteModal(false)}
                        className="flex-1 btn-secondary text-sm py-2">
                        Cancelar
                      </button>
                      <button type="submit" disabled={inviting || !inviteForm.email || !inviteForm.department_name}
                        className="flex-1 btn-primary text-sm py-2 disabled:opacity-50">
                        {inviting ? 'Enviando...' : 'Enviar invitación'}
                      </button>
                    </div>
                  </form>
                ) : inviteResult.success ? (
                  <div className="space-y-4">
                    <div className="rounded-xl p-4 text-center" style={{ background: 'rgba(0,208,132,0.07)', border: '1px solid rgba(0,208,132,0.2)' }}>
                      <p className="text-2xl mb-2">✉️</p>
                      <p className="text-sm font-bold text-foreground mb-1">Invitación enviada</p>
                      <p className="text-xs text-muted-foreground">{inviteResult.email}</p>
                      {!inviteResult.email_sent && (
                        <p className="text-xs mt-2" style={{ color: '#F59E0B' }}>
                          ⚠ El email no se envió. Comparte el link manualmente:
                        </p>
                      )}
                    </div>
                    {(!inviteResult.email_sent && inviteResult.signup_link) && (
                      <div className="rounded-lg p-3" style={{ background: 'rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.08)' }}>
                        <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground mb-1.5">Link de registro</p>
                        <p className="text-xs break-all font-mono" style={{ color: '#6366F1' }}>{inviteResult.signup_link}</p>
                        <button
                          onClick={() => navigator.clipboard.writeText(inviteResult.signup_link)}
                          className="mt-2 text-[10px] font-bold px-2.5 py-1 rounded-lg transition-colors"
                          style={{ background: 'rgba(99,102,241,0.1)', color: '#6366F1' }}>
                          Copiar link
                        </button>
                      </div>
                    )}
                    <button onClick={() => { setInviteResult(null); setInviteForm({ email: '', role: 'Usuario', department_name: '' }); }}
                      className="w-full btn-secondary text-sm py-2">
                      Invitar otro usuario
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="rounded-xl p-4 text-center" style={{ background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.2)' }}>
                      <p className="text-2xl mb-2">⚠️</p>
                      <p className="text-sm font-bold text-foreground mb-1">Error</p>
                      <p className="text-xs text-muted-foreground">{inviteResult.error}</p>
                    </div>
                    <button onClick={() => setInviteResult(null)} className="w-full btn-secondary text-sm py-2">
                      Intentar de nuevo
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Form Modal */}
        {showForm && (
          <>
            <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowForm(false)} />
            <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
              <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
                <h3 className="text-lg font-semibold text-foreground mb-4">
                  {editingItem ? 'Editar' : 'Crear'} {tabs.find(t => t.key === activeTab)?.label}
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1 text-foreground">
                      Responsable <span className="text-destructive">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name || ''}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      placeholder="Ej: Carlos Martínez"
                      className="w-full px-3 py-2 bg-background border border-input rounded-md"
                    />
                  </div>

                  {activeTab === 'users' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium mb-1 text-foreground">Email</label>
                        <input
                          type="email"
                          value={formData.email || ''}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                          className="w-full px-3 py-2 bg-background border border-input rounded-md"
                        />
                      </div>
                      {!editingItem && (
                        <div>
                          <label className="block text-sm font-medium mb-1 text-foreground">Contraseña</label>
                          <input
                            type="password"
                            value={formData.password || ''}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            required
                            className="w-full px-3 py-2 bg-background border border-input rounded-md"
                          />
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium mb-1 text-foreground">Rol</label>
                        <select
                          value={formData.role || 'Usuario'}
                          onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                          className="w-full px-3 py-2 bg-background border border-input rounded-md"
                        >
                          <option value="Presidencia">Presidencia</option>
                          <option value="Gerencia">Gerencia</option>
                          <option value="Usuario">Usuario</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-bold mb-1 text-foreground">
                          Departamento <span className="text-destructive">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.department_name || ''}
                          onChange={(e) => setFormData({ ...formData, department_name: e.target.value })}
                          required
                          className="input-decko w-full"
                          placeholder="Ej: Operaciones, Logística, Comercial"
                        />
                      </div>
                    </>
                  )}

                  {activeTab === 'tags' && (
                    <div>
                      <label className="block text-sm font-medium mb-1 text-foreground">Color</label>
                      <input
                        type="color"
                        value={formData.color || '#689DA4'}
                        onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                        className="w-full h-10 bg-background border border-input rounded-md"
                      />
                    </div>
                  )}

                  {activeTab === 'departments' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium mb-1 text-foreground">Área</label>
                        <select
                          value={formData.area_id || ''}
                          onChange={(e) => setFormData({ ...formData, area_id: e.target.value })}
                          required
                          className="w-full px-3 py-2 bg-background border border-input rounded-md"
                        >
                          <option value="">Seleccionar...</option>
                          {areas.map(a => (
                            <option key={a.id} value={a.id}>{a.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1 text-foreground">
                          Encargado <span className="text-destructive">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.manager_name || ''}
                          onChange={(e) => setFormData({ ...formData, manager_name: e.target.value })}
                          required
                          placeholder="Ej: Carlos"
                          className="w-full px-3 py-2 bg-background border border-input rounded-md"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Nombre del encargado</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1 text-foreground">
                          Vincular a Usuario (Opcional)
                        </label>
                        <select
                          value={formData.manager_user_id || ''}
                          onChange={(e) => {
                            const userId = e.target.value;
                            const user = users.find(u => u.id === userId);
                            setFormData({ 
                              ...formData, 
                              manager_user_id: userId || null,
                              manager_name: user ? user.name : formData.manager_name
                            });
                          }}
                          className="w-full px-3 py-2 bg-background border border-input rounded-md"
                        >
                          <option value="">Sin vincular</option>
                          {users.map(u => (
                            <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                          ))}
                        </select>
                        <p className="text-xs text-muted-foreground mt-1">
                          Autocompleta nombre pero puedes editarlo
                        </p>
                      </div>
                    </>
                  )}

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      className="flex-1 px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:opacity-90"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:opacity-90"
                    >
                      Guardar
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Admin;
